import React, { useState } from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import type {
  RevenueReportData,
  IspComparison,
  RevenueComponent,
  RevenueChargeback,
} from './use-finance-revenue.js';

function fmt(n: number | null | undefined): string {
  if (n == null) return '\u2014';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function fmtPrecise(n: number | null | undefined): string {
  if (n == null) return '\u2014';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
}

function fmtDate(val: string | null | undefined): string {
  if (!val) return '\u2014';
  const d = new Date(val + 'T12:00:00');
  if (isNaN(d.getTime())) return val;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function ComponentRow({ item, total }: { item: RevenueComponent; total: number }) {
  const pct = total > 0 ? (item.amount / total) * 100 : 0;
  const label = item.component.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  return (
    <div style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}`, display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center' }}>
      <div>
        <div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 12, color: C.textMuted }}>
          {item.stops.toLocaleString()} stops
          {item.stops > 0 ? ` \u00b7 $${(item.amount / item.stops).toFixed(2)}/stop` : ''}
        </div>
        <div style={{ height: 4, background: C.borderSoft, borderRadius: 2, marginTop: 6, width: '100%' }}>
          <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: C.gold, borderRadius: 2 }} />
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{fmt(item.amount)}</div>
        <div style={{ fontSize: 11, color: C.textMuted }}>{pct.toFixed(1)}%</div>
      </div>
    </div>
  );
}

function IspCard({ isp }: { isp: IspComparison }) {
  const [expanded, setExpanded] = useState(false);
  const hasCharges = isp.charge_detail.length > 0;

  return (
    <div style={{ borderRadius: 10, border: `1px solid ${C.borderSoft}`, overflow: 'hidden' }}>
      <button
        onClick={() => hasCharges && setExpanded(!expanded)}
        style={{
          width: '100%', padding: '12px 16px', background: C.panel, border: 'none',
          display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center',
          cursor: hasCharges ? 'pointer' : 'default', color: C.text, textAlign: 'left',
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            ISP {isp.isp_number}{isp.terminal ? ` \u00b7 ${isp.terminal}` : ''}
          </div>
          <div style={{ fontSize: 12, color: C.textMuted, display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 4 }}>
            <span>Net: <span style={{ color: C.green }}>{fmt(isp.net_settlement)}</span></span>
            <span>Charges: {fmt(isp.total_charges)}</span>
            {isp.chargebacks !== 0 && (
              <span style={{ color: C.red }}>Chargebacks: {fmt(isp.chargebacks)}</span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18, fontWeight: 700, color: C.gold }}>{fmt(isp.net_settlement)}</span>
          {hasCharges && <span style={{ fontSize: 12, color: C.textMuted }}>{expanded ? '\u25b2' : '\u25bc'}</span>}
        </div>
      </button>
      {expanded && isp.charge_detail.length > 0 && (
        <div style={{ padding: '0 16px 12px', background: C.panel }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                {['Type', 'Description', 'Date', 'Amount'].map(h => (
                  <th key={h} style={{ padding: '6px 8px', textAlign: 'left', color: C.textMuted, fontWeight: 500, fontSize: 10, textTransform: 'uppercase', borderBottom: `1px solid ${C.borderSoft}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isp.charge_detail.map((cd, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                  <td style={{ padding: '6px 8px', color: C.text }}>{cd.charge_type || '\u2014'}</td>
                  <td style={{ padding: '6px 8px', color: C.textMuted }}>{cd.description || '\u2014'}</td>
                  <td style={{ padding: '6px 8px', color: C.textMuted }}>{fmtDate(cd.date)}</td>
                  <td style={{ padding: '6px 8px', color: cd.amount < 0 ? C.red : C.text, fontWeight: 600 }}>{fmtPrecise(cd.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ChargebackRow({ cb }: { cb: RevenueChargeback }) {
  return (
    <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(255,107,107,0.06)', border: `1px solid rgba(255,107,107,0.15)`, display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center' }}>
      <div>
        <div style={{ fontSize: 13, color: C.text }}>
          {cb.charge_type || 'Chargeback'}{cb.isp_number ? ` \u00b7 ISP ${cb.isp_number}` : ''}
        </div>
        <div style={{ fontSize: 11, color: C.textMuted }}>
          {cb.description || ''}{cb.date ? ` \u00b7 ${fmtDate(cb.date)}` : ''}
        </div>
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: C.red }}>{fmtPrecise(cb.amount)}</div>
    </div>
  );
}

interface FinanceRevenueReportProps {
  data: RevenueReportData;
  onRefresh: () => void;
}

export default function FinanceRevenueReport({ data, onRefresh }: FinanceRevenueReportProps) {
  const { components, isp_comparison, chargebacks, non_scans, period } = data;

  const totalRevenue = isp_comparison.reduce((s, isp) => s + isp.net_settlement, 0);
  const totalChargebacks = chargebacks.reduce((s, cb) => s + cb.amount, 0);
  const totalStops = components.reduce((s, c) => s + c.stops, 0);
  const componentTotal = components.reduce((s, c) => s + c.amount, 0);

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{fmt(totalRevenue)}</div>
          <div style={{ fontSize: 12, color: C.textMuted }}>
            Net revenue \u00b7 {period.weeks_included} week{period.weeks_included !== 1 ? 's' : ''}
            {totalStops > 0 ? ` \u00b7 ${totalStops.toLocaleString()} stops` : ''}
          </div>
        </div>
        <button
          onClick={onRefresh}
          style={{ background: 'none', border: `1px solid ${C.borderSoft}`, borderRadius: 6, color: C.textMuted, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}
        >
          Refresh
        </button>
      </div>

      {/* Revenue components */}
      {components.length > 0 && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 10 }}>
            Revenue components
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            {components.map((item) => (
              <ComponentRow key={item.component} item={item} total={componentTotal} />
            ))}
          </div>
        </div>
      )}

      {/* ISP comparison */}
      {isp_comparison.length > 0 && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 10 }}>
            Revenue by ISP ({isp_comparison.length})
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            {isp_comparison.sort((a, b) => b.net_settlement - a.net_settlement).map((isp) => (
              <IspCard key={isp.isp_number} isp={isp} />
            ))}
          </div>
        </div>
      )}

      {/* Chargebacks */}
      {chargebacks.length > 0 && (
        <div style={LAYOUT.subPanel}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
            <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.red }}>
              Chargebacks ({chargebacks.length})
            </div>
            <div style={{ fontSize: 13, color: C.red, fontWeight: 700 }}>{fmt(totalChargebacks)}</div>
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {chargebacks.map((cb, i) => (
              <ChargebackRow key={i} cb={cb} />
            ))}
          </div>
        </div>
      )}

      {/* Non-scans */}
      {non_scans.length > 0 && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#FBBF24', marginBottom: 10 }}>
            Non-scan entries ({non_scans.length})
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {non_scans.map((ns, i) => (
              <div key={i} style={{ padding: '8px 12px', borderRadius: 8, background: C.panel, border: `1px solid ${C.borderSoft}`, display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 13, color: C.text }}>
                    {ns.isp_number ? `ISP ${ns.isp_number}` : 'Unknown'} \u00b7 {fmtDate(ns.date)}
                  </div>
                  <div style={{ fontSize: 11, color: C.textMuted }}>{Math.abs(ns.stops)} missed scan{Math.abs(ns.stops) !== 1 ? 's' : ''}</div>
                </div>
                <div style={{ fontSize: 13, color: '#FBBF24', fontWeight: 600 }}>{fmtPrecise(ns.amount)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
